<template>
    <OffersTableWrapper>
        <template #actions>
            <CreateButton
                v-if="userStore.can('manage_offers')"
                @click.stop="openCreateOfferModal"
            />
        </template>
        <template #filters>
            <DropdownFilter
                :pin="pinFilters"
                @apply="applyFilters"
                @clear="clearFilters"
                @toggle-pin="togglePinFilters"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <TextField
                            v-model="filtersFields.id"
                            name="id"
                            :placeholder="$t('id')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <TextField
                            v-model="filtersFields.payment_reference"
                            name="payment_reference"
                            :placeholder="$t('payment_reference')"
                        />
                    </label>
                </DropdownFilterItem>

                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.asset_uid"
                            v-model:options="assetSelectOptions"
                            name="asset_uid"
                            class="w-full"
                            :placeholder="$t('asset')"
                            :on-search="loadAssetSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.buyer_id"
                            :options="buyerFilterOptions"
                            name="buyer_id"
                            class="w-full"
                            :placeholder="$t('buyer')"
                            :on-search="loadBuyerFilterOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.seller_id"
                            :options="sellerFilterOptions"
                            name="seller_id"
                            class="w-full"
                            :placeholder="$t('seller')"
                            :on-search="loadSellerFilterOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.offer_status"
                            name="offer_status"
                            class="w-full"
                            :options="statusOptions"
                            :placeholder="$t('offer_status')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.transaction_status"
                            name="transaction_status"
                            class="w-full"
                            :options="transactionStatusOptions"
                            :placeholder="$t('transaction_status')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </OffersTableWrapper>

    <ModalWrapper v-if="isModalOpen" />

    <!-- Create or Edit Offer Modal -->
    <CreateOrEditOfferModal
        v-if="createOrEditOfferModalOpen"
        v-model:open="createOrEditOfferModalOpen"
        :offer="tempOffer"
        :platform-name="initialFilters?.platform_name_exact"
        @submit="handleOfferSubmit"
        @cancel="closeOfferModal"
    />

    <!-- Regenerate Offer Modal -->
    <WarningModal
        v-if="tempOfferToRegenerate"
        id="regenerate-offer-modal"
        :title="$t('warning')"
        :open="regenerateOfferModalOpen"
        :action-label="$t('modal_title_regenerate_offer')"
        @cancel="closeRegenerateOfferModal"
        @continue="handleRegenerateOffer"
    />
</template>

<script setup lang="ts">
import { useModal, useTable } from '@composables/common'
import { TransactionStatus } from '@enums/SecondaryMarket'
import { formatSecondaryMarketValue } from '@partials/secondaryMarket/formatSecondaryMarketValue'
import {
    DropdownFilter,
    DropdownFilterItem,
    SelectField,
    TextField,
} from '@src/components'
import WarningModal from '@src/components/WarningModal.vue'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import CreateOrEditOfferModal from '@src/partials/secondaryMarket/CreateOrEditOfferModal.vue'
import { Account, Asset, Offer, TableSorting } from '@src/types'
import { getAccounts } from '@src/utils/api/accounts'
import { getAssets } from '@src/utils/api/assets'
import {
    createOffer as apiCreateOffer,
    regenerateOffer as apiRegenerateOffer,
    getOffers,
    sendOfferEmail,
} from '@src/utils/api/secondaryMarket'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { h, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface SecondaryMarketOfferRecord extends Offer {
    seller_title?: string
    seller_email?: string
    seller_account_type?: string
    seller_profile_id?: string
    buyer_title?: string
    buyer_email?: string
    buyer_account_type?: string
    buyer_profile_id?: string
    asset_id?: string
    asset_title?: string
    created_at?: string
}

const props = withDefaults(
    defineProps<{
        initialFilters?: Record<string, any> | null
    }>(),
    {
        initialFilters: null,
    }
)

const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const { ModalWrapper, openModal } = useModal()

const offers = ref<SecondaryMarketOfferRecord[]>([])
const offersCount = ref<number>(0)
const assetSelectOptions = ref<{ label: string; value: string | null }[]>([])
const sellerFilterOptions = ref<{ label: string; value: string | null }[]>([])
const buyerFilterOptions = ref<{ label: string; value: string | null }[]>([])

const tempOffer = ref<SecondaryMarketOfferRecord | null>(null)
const createOrEditOfferModalOpen = ref(false)

const tempOfferToRegenerate = ref<SecondaryMarketOfferRecord | null>(null)
const regenerateOfferModalOpen = ref(false)

const isModalOpen = ref(false)
const emailTargetOffer = ref<SecondaryMarketOfferRecord | null>(null)
const emailTypeToSend = ref<string | null>(null)
const emailRecipientRole = ref<string | null>(null)

// `requiresListing`/`requiresBuyRequest` restrict a type to offers of that origin only
// (an offer always has exactly one of listing_id / buy_request_id set).
const OFFER_EMAIL_TYPES = [
    {
        value: 'sm_offer_received',
        requiresTransaction: false,
        requiresListing: true,
    },
    {
        value: 'sm_buy_request_received',
        requiresTransaction: false,
        requiresBuyRequest: true,
    },
    { value: 'sm_offer_rejected', requiresTransaction: false },
    { value: 'sm_sign_process_completed', requiresTransaction: true },
    { value: 'sm_transaction_completed', requiresTransaction: true },
    {
        value: 'sm_transaction_waiting_for_approval',
        requiresTransaction: true,
    },
    { value: 'sm_transaction_failed', requiresTransaction: true },
    { value: 'sm_buyer_payment_paid', requiresTransaction: true },
]

const recipientRoleOptions = [
    { label: i18n.t('role_buyer'), value: 'buyer' },
    { label: i18n.t('role_seller'), value: 'seller' },
]

const statusOptions = [
    { label: i18n.t('offer_status_pending'), value: 'pending' },
    { label: i18n.t('offer_status_accepted'), value: 'accepted' },
    { label: i18n.t('offer_status_rejected'), value: 'rejected' },
    { label: i18n.t('offer_status_cancelled'), value: 'cancelled' },
    { label: i18n.t('offer_status_expired'), value: 'expired' },
]

const transactionStatusOptions = Object.values(TransactionStatus).map(
    (status) => ({
        label: i18n.t(`transaction_status_${status}`),
        value: status,
    })
)

const {
    TableWrapper: OffersTableWrapper,
    selected,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    pinFilters,
    togglePinFilters,
    setFilters,
    applyFilters,
    clearFilters,
} = useTable({
    loadData: loadOffers,
    filters: {
        id: null,
        asset_uid: null,
        buyer_id: null,
        seller_id: null,
        offer_status: null,
        transaction_status: null,
    },
    tableProps: {
        id: 'offers-table',
        label: 'secondary_market_offers',
        help: 'help_offers_table',
        pagination: true,
        data: offers,
        total: offersCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'id',
                key: 'id',
                condition: userStore.can('see_id_columns'),
                copy: true,
            },
            {
                label: 'payment_reference',
                key: 'payment_reference',
                sorting: true,
                copy: true,
            },
            {
                label: 'seller',
                key: 'seller_title',
                url: (row: SecondaryMarketOfferRecord) =>
                    accountUrl(row.seller_profile_id, row.seller_account_type),
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return (
                        props.rowData.seller_title ||
                        props.rowData.seller_email ||
                        props.rowData.seller_id ||
                        '-'
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'buyer',
                key: 'buyer_title',
                url: (row: SecondaryMarketOfferRecord) =>
                    accountUrl(row.buyer_profile_id, row.buyer_account_type),
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return (
                        props.rowData.buyer_title ||
                        props.rowData.buyer_email ||
                        props.rowData.buyer_id ||
                        '-'
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'quantity',
                key: 'quantity',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return formatSecondaryMarketValue(props.rowData.quantity)
                },
            },
            {
                label: 'price_per_unit',
                key: 'price_per_unit',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return props.rowData.price_per_unit
                        ? `€ ${formatSecondaryMarketValue(
                              props.rowData.price_per_unit
                          )}`
                        : '-'
                },
            },
            {
                label: 'total_price',
                key: 'total_price',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return props.rowData.total_price
                        ? `€ ${formatSecondaryMarketValue(
                              props.rowData.total_price
                          )}`
                        : '-'
                },
            },
            {
                label: 'offer_status',
                key: 'offer_status',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return props.rowData.offer_status
                        ? i18n.t(`offer_status_${props.rowData.offer_status}`)
                        : '-'
                },
            },
            {
                label: 'transaction_status',
                key: 'transaction_status',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return props.rowData.transaction_status
                        ? i18n.t(
                              `transaction_status_${props.rowData.transaction_status}`
                          )
                        : '-'
                },
            },
            {
                label: 'created_at',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketOfferRecord
                }) => {
                    return props.rowData.created_at
                        ? moment(props.rowData.created_at).format(
                              'DD.MM.YYYY HH:mm'
                          )
                        : '-'
                },
            },
        ],
        actions: [
            {
                action: (rowData: SecondaryMarketOfferRecord) => {
                    tempOfferToRegenerate.value = rowData
                    regenerateOfferModalOpen.value = true
                },
                icon: 'fa-solid fa-rotate-right',
                title: 'regenerate',
                condition: (row: SecondaryMarketOfferRecord) =>
                    userStore.can('manage_offers') && !!row.buy_request_id,
            },
            {
                action: (rowData: SecondaryMarketOfferRecord) => {
                    tempOffer.value = { ...rowData }
                    createOrEditOfferModalOpen.value = true
                },
                icon: 'fa-solid fa-pen',
                title: 'edit',
                condition: () => userStore.can('manage_offers'),
            },
            {
                action: (rowData: SecondaryMarketOfferRecord) => {
                    openSendEmailModal(rowData)
                },
                icon: 'fa-solid fa-envelope',
                title: 'send_email',
                condition: () => userStore.can('manage_offers'),
            },
        ],
    },
})

async function loadOffers() {
    try {
        const response = await getOffers(
            filters.value,
            page.value,
            limit.value,
            sorting.value
        )
        const responseData = response.data || response

        offers.value = (responseData.offers ||
            []) as SecondaryMarketOfferRecord[]
        offersCount.value = responseData.offers_count || 0
        selected.value = []
    } catch (error) {
        console.error('Failed to load offers:', error)
    }
}

async function loadAssetSelectOptions(search: string) {
    const assetsRequest = await getAssets({
        'filter[asset_title]': search,
    })
    assetSelectOptions.value = assetsRequest.data.assets.map(
        (asset: Asset) => ({
            label: asset.asset_title as string,
            value: asset.asset_uid as string,
        })
    )
}

async function loadSellerFilterOptions(search: string) {
    const accountsRequest = await getAccounts({
        filter: {
            search: search,
            platform_name_exact: props.initialFilters?.platform_name_exact,
        },
    })
    sellerFilterOptions.value = accountsRequest.data.accounts.map(
        (account: Account) => ({
            label: `${account.account_title} (${account.email})`,
            value: account.account_uid as string,
        })
    )
}

async function loadBuyerFilterOptions(search: string) {
    const accountsRequest = await getAccounts({
        filter: {
            search: search,
            platform_name_exact: props.initialFilters?.platform_name_exact,
        },
    })
    buyerFilterOptions.value = accountsRequest.data.accounts.map(
        (account: Account) => ({
            label: `${account.account_title} (${account.email})`,
            value: account.account_uid as string,
        })
    )
}

function openCreateOfferModal() {
    tempOffer.value = null
    createOrEditOfferModalOpen.value = true
}

function closeOfferModal() {
    createOrEditOfferModalOpen.value = false
    tempOffer.value = null
}

function openSendEmailModal(offer: SecondaryMarketOfferRecord) {
    emailTargetOffer.value = offer
    emailTypeToSend.value = null
    emailRecipientRole.value = null
    isModalOpen.value = true

    const hasTransaction = !!offer.transaction_status
    const isFromListing = !!offer.listing_id
    const isFromBuyRequest = !!offer.buy_request_id
    const typeOptions = OFFER_EMAIL_TYPES.filter((emailType) => {
        if (emailType.requiresTransaction && !hasTransaction) return false
        if (emailType.requiresListing && !isFromListing) return false
        if (emailType.requiresBuyRequest && !isFromBuyRequest) return false
        return true
    }).map((emailType) => ({
        // sm_buy_request_received reuses the platform-wide EMAIL_TEMPLATE_* label
        // ("New buy request received"), which reads as if a buy request itself
        // just arrived. In this dropdown it actually means "an offer arrived for
        // the buyer's existing buy request", so it gets a dedicated, clearer label.
        label:
            emailType.value === 'sm_buy_request_received'
                ? i18n.t('send_email_option_offer_received_buy_request')
                : i18n.t(`EMAIL_TEMPLATE_${emailType.value.toUpperCase()}`),
        value: emailType.value,
    }))

    openModal(
        {
            id: 'offer_send_email',
            title: 'modal_title_send_offer_email',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: handleSendEmail,
        },
        () => {
            const fields = [
                h(SelectField, {
                    'name': 'type',
                    'label': 'email_template_type',
                    'help': 'help_send_offer_email',
                    'modelValue': emailTypeToSend.value,
                    'onUpdate:modelValue': (value: string) => {
                        emailTypeToSend.value = value
                    },
                    'options': typeOptions,
                    'required': true,
                }),
            ]

            if (emailTypeToSend.value === 'sm_transaction_failed') {
                fields.push(
                    h(SelectField, {
                        'name': 'recipient_role',
                        'label': 'recipient_role',
                        'modelValue': emailRecipientRole.value,
                        'onUpdate:modelValue': (value: string) => {
                            emailRecipientRole.value = value
                        },
                        'options': recipientRoleOptions,
                    })
                )
            }

            return h('div', null, fields)
        }
    )
}

async function handleSendEmail() {
    if (!emailTargetOffer.value || !emailTypeToSend.value) {
        return
    }

    try {
        const response = await sendOfferEmail(
            emailTargetOffer.value.id as string,
            emailTypeToSend.value,
            emailRecipientRole.value || undefined
        )
        toast.success(response.data.message)
    } catch (error) {
        toast.error(i18n.t(error))
    } finally {
        isModalOpen.value = false
        emailTargetOffer.value = null
        emailTypeToSend.value = null
        emailRecipientRole.value = null
    }
}

async function handleOfferSubmit(offerData: any) {
    try {
        await apiCreateOffer(offerData)
        toast.success(i18n.t('toast_success_offer_saved'))
        closeOfferModal()
        await loadOffers()
    } catch (error) {
        toast.error(i18n.t(error))
    }
}

function closeRegenerateOfferModal() {
    regenerateOfferModalOpen.value = false
    tempOfferToRegenerate.value = null
}

async function handleRegenerateOffer() {
    if (!tempOfferToRegenerate.value?.id) {
        toast.error(i18n.t('toast_error_cannot_regenerate_offer'))
        return
    }

    try {
        await apiRegenerateOffer(tempOfferToRegenerate.value.id)
        toast.success(i18n.t('toast_success_offer_regenerated'))
        closeRegenerateOfferModal()
        await loadOffers()
    } catch (error) {
        toast.error(i18n.t(error))
    }
}

function accountUrl(profileId?: string, profileType?: string) {
    if (!profileId || !profileType) return '#'
    return `/${profileType}Profile/${profileId}`
}

onMounted(() => {
    if (props.initialFilters) {
        filtersFields.value = {
            ...filtersFields.value,
            ...props.initialFilters,
        }
        setFilters()
    }
    loadOffers()
})
</script>
